//
//  SDKSupernovaError.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
const SupernovaNoNetworkResponse = "NO_NETWORK";
const SupernovaInvalidResponseBody = "InvalidResponseBody";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Function Definition
/** Supernova-enhanced error. Contains both machine and user readable information about error */
export class SupernovaError extends Error {
    constructor(message, code, status, formErrors, requestId, serverTime, url) {
        super(message);
        this.code = code;
        this.status = status;
        // @ts-expect-error TS(2322): Type 'SupernovaFieldValidationError[] | null' is n... Remove this comment to see the full error message
        this.formErrors = formErrors;
        this.requestId = requestId;
        this.serverTime = serverTime;
        this.url = url;
    }
    /** Creates Supernova error from received but failed network response */
    static fromNetworkResponse(response, status, url) {
        var _a, _b, _c;
        return new SupernovaError(response.message, response.errorCode || response.reason || response.label || null, response.code || status, (_a = response.formErrors) !== null && _a !== void 0 ? _a : null, (_b = response.requestId) !== null && _b !== void 0 ? _b : null, (_c = response.serverTime) !== null && _c !== void 0 ? _c : null, url);
    }
    /**
     * Creates Supernova error when a failed response body is not valid JSON.
     * Preserves the real HTTP status so callers can still detect 401/404 etc.
     */
    static fromUnparseableResponse(status, url, rawBodySnippet) {
        const detail = rawBodySnippet ? `: ${rawBodySnippet}` : "";
        return new SupernovaError(`Request failed with status ${status}${detail}`, SupernovaInvalidResponseBody, status, null, null, null, url);
    }
    /** Creates Supernova error with generic network failure message */
    static fromNoNetworkResponse(url) {
        return new SupernovaError("No network or internet unreachable", SupernovaNoNetworkResponse, 12163, // Internet connection lost
        null, new Date().toISOString(), null, url);
    }
    /** Creates Supernova error from local validation failure code */
    static fromCode(code) {
        // Error was thrown in the processing layer - tooling etc.
        return new SupernovaError(code, code, null, null, null, null, null);
    }
    /** Creates Supernova error from generic error message */
    static fromMessage(message) {
        return new SupernovaError(message, null, null, null, null, null, null);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Conveniences
    /** Checks whether error contains some field validation error */
    isFieldErrored(key) {
        return this.formErrors.find((f) => f.key === key) !== undefined;
    }
    /** Retrieves error for a specific field, or null if no error is present for specific field */
    errorForField(key) {
        var _a;
        return (_a = this.formErrors.find((f) => f.key === key)) !== null && _a !== void 0 ? _a : null;
    }
    /** Returns all important information about the error, ideally suited for logging to console or sentry */
    toString() {
        var _a, _b, _c, _d, _e, _f;
        return `
    Error thrown: ${this.message}\n
    Code: ${(_a = this.code) !== null && _a !== void 0 ? _a : "-"}\n
    Status: ${(_b = this.status) !== null && _b !== void 0 ? _b : "-"}\n
    Fields: ${JSON.stringify(this.formErrors, null, 2)}\n
    Request ID: ${(_c = this.requestId) !== null && _c !== void 0 ? _c : "-"}\n
    Server time: ${(_d = this.serverTime) !== null && _d !== void 0 ? _d : "-"}\n
    Network URL: ${(_e = this.url) !== null && _e !== void 0 ? _e : "-"}\n
    Stack: ${(_f = this.stack) !== null && _f !== void 0 ? _f : "-"}
    `;
    }
    /** Returns error with most important information to show to the users */
    toHumanReadable() {
        return `${this.message}${this.requestId ? ` (request ${this.requestId})` : ""}`;
    }
}
/**
 * Builds a SupernovaError from a failed response body, tolerating non-JSON bodies.
 * A non-JSON body (HTML gateway page, plain text) no longer throws a SyntaxError;
 * it becomes a typed error that keeps the HTTP status.
 */
export function parseErrorResponseBody(status, url, rawText) {
    const trimmed = rawText.trim();
    if (trimmed.length === 0) {
        // No body to parse, but keep the status so 401/404 stay detectable
        return SupernovaError.fromUnparseableResponse(status, url);
    }
    let parsed;
    try {
        parsed = JSON.parse(trimmed);
    }
    catch (_a) {
        // Body was not JSON, e.g. an HTML error page or plain "Not found"
        return SupernovaError.fromUnparseableResponse(status, url, trimmed.slice(0, 200));
    }
    if (parsed && typeof parsed === "object") {
        return SupernovaError.fromNetworkResponse(parsed, status, url);
    }
    // Valid JSON but not an object, e.g. a bare string or number
    return SupernovaError.fromUnparseableResponse(status, url, trimmed.slice(0, 200));
}
